
import { collection, addDoc, query, where, onSnapshot, updateDoc, doc, orderBy, limit } from 'firebase/firestore';
import { ref, push } from 'firebase/database';
import { Notification, NotificationType, NotificationStatus } from '../types/user';
import { db, realtimeDb } from './firebase';

/**
 * Helper utility to get real user profiles or fallback to dummy profiles
 */

// Dummy profile generator for fallback
const dummyAvatars = [
  "https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png",
  "https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png",
  "https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png",
  // "https://randomuser.me/api/portraits/women/46.jpg",
  // "https://randomuser.me/api/portraits/men/47.jpg",
  // "https://randomuser.me/api/portraits/women/48.jpg",
  // "https://randomuser.me/api/portraits/men/49.jpg",
  // "https://randomuser.me/api/portraits/women/50.jpg",
];

export interface ParticipantProfile {
  id?: number;
  email: string;
  first_name?: string;
  last_name?: string;
  profile_picture?: string | null;
  accepted?: boolean;
  is_moderator?: boolean;
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    profile_picture?: string;
  };
}

/**
 * Get participant profile with real data or fallback to dummy profile
 * @param participant - Participant data from backend
 * @param index - Index for dummy avatar selection
 * @returns Enhanced participant profile with avatar
 */
export const getParticipantProfile = (
  participant: any,
  index: number
): ParticipantProfile & { avatar: string; displayName: string } => {
  // If user data exists, use real profile
  if (participant.user) {
    return {
      ...participant,
      avatar: participant.user.profile_picture
        ? `${process.env.REACT_APP_BACKEND_URL}${participant.user.profile_picture}`
        : dummyAvatars[index % dummyAvatars.length],
      displayName: `${participant.user.first_name} ${participant.user.last_name}`,
    };
  }

  // Fallback to dummy profile if no user data
  return {
    ...participant,
    avatar: dummyAvatars[index % dummyAvatars.length],
    displayName: participant.email.split("@")[0], // Use email prefix as name
    first_name: participant.email.split("@")[0],
    last_name: "User",
  };
};

/**
 * Get avatar URL for a participant
 * Prefers real profile picture, falls back to dummy
 */
export const getParticipantAvatar = (
  participant: any,
  index: number
): string => {
  // Check if user has profile picture
  if (participant.user?.profile_picture) {
    return `${process.env.REACT_APP_BACKEND_URL}${participant.user.profile_picture}`;
  }

  // Check if participant has profile picture directly
  if (participant.profile_picture) {
    return `${process.env.REACT_APP_BACKEND_URL}${participant.profile_picture}`;
  }

  // Fallback to dummy avatar
  return dummyAvatars[index % dummyAvatars.length];
};

/**
 * Get display name for a participant
 * Prefers real name, falls back to email
 */
export const getParticipantDisplayName = (participant: any): string => {
  // If user data exists, use real name
  if (participant.user?.first_name && participant.user?.last_name) {
    return `${participant.user.first_name} ${participant.user.last_name}`;
  }

  // If participant has name stored
  if (participant.participant_name) {
    return participant.participant_name;
  }

  // Fallback to email
  return participant.email;
};

/**
 * Get email for a participant
 */
export const getParticipantEmail = (participant: any): string => {
  return participant.user?.email || participant.email || "unknown@example.com";
};

/**
 * Check if participant has real user profile
 */
export const hasRealProfile = (participant: any): boolean => {
  return !!(participant.user && participant.user.id);
};

// export const getUnreadNotificationCount = (
//   userId: string,
//   callback: (count: number) => void
// ) => {
//   const q = query(
//     collection(db, 'notifications'),
//     where('userId', '==', userId),
//     where('status', '==', NotificationStatus.UNREAD)
//   );

//   const unsubscribe = onSnapshot(q, (querySnapshot) => {
//     callback(querySnapshot.size);
//   });

//   return unsubscribe;
// };

export const runNotificationTests = async (userId: string) => {
  console.log('Running notification tests...');

  try {
    // Test 1: Create a meeting reminder
    const reminderId = await createMeetingReminder(
      userId,
      'meeting123',
      'Project Kick-off',
      '4:00 PM'
    );
    console.log('Created meeting reminder with ID:', reminderId);

    // Test 2: Create a meeting invitation
    const invitationId = await createMeetingInvitation(
      userId,
      'meeting456',
      'Design Sync',
      'Jane Doe'
    );
    console.log('Created meeting invitation with ID:', invitationId);

    console.log('Notification tests completed successfully!');
  } catch (error) {
    console.error('Notification tests failed:', error);
  }
};

/**
 * Notification service for real-time notifications using Firebase
 */
// import { db, realtimeDb } from './participantProfileHelper';

export interface CreateNotificationInput {
  type: NotificationType;
  title: string;
  message: string;
  userId: string;
  meetingId?: string;
  data?: any;
}

/**
 * Create a new notification in Firestore
 */
export const createNotification = async (input: CreateNotificationInput): Promise<string> => {
  try {
    const notificationData = {
      ...input,
      status: NotificationStatus.UNREAD,
      createdAt: new Date().toISOString(),
    };

    const docRef = await addDoc(collection(db, 'notifications'), notificationData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

/**
 * Get real-time notifications for a user
 */



export const subscribeToUserNotifications = (
  userId: string,
  callback: (notifications: Notification[]) => void
) => {
  const q = query(
    collection(db, 'notifications'),
    where('userId', '==', userId),
    where('status', '!=', NotificationStatus.DISMISSED),
    orderBy('createdAt', 'desc'),
    limit(50)
  );

  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    const notifications: Notification[] = [];
    querySnapshot.forEach((doc) => {
      notifications.push({
        id: doc.id,
        ...doc.data()
      } as Notification);
    });
    callback(notifications);
  });

  return unsubscribe;
};

/**
 * Mark notification as read
 */
export const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      status: NotificationStatus.READ,
      readAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Dismiss notification
 */
export const dismissNotification = async (notificationId: string): Promise<void> => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      status: NotificationStatus.DISMISSED,
      dismissedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error dismissing notification:', error);
    throw error;
  }
};

/**
 * Create meeting reminder notification
 */
export const createMeetingReminder = async (
  userId: string,
  meetingId: string,
  meetingTitle: string,
  startTime: string
): Promise<string> => {
  return createNotification({
    type: NotificationType.MEETING_REMINDER,
    title: `Meeting Starting Soon`,
    message: `${meetingTitle} starts in 5 minutes at ${startTime}`,
    userId,
    meetingId,
    data: {
      startTime,
      reminderMinutes: 5
    }
  });
};

/**
 * Create meeting invitation notification
 */
export const createMeetingInvitation = async (
  userId: string,
  meetingId: string,
  meetingTitle: string,
  inviterName: string
): Promise<string> => {
  return createNotification({
    type: NotificationType.MEETING_INVITATION,
    title: `Meeting Invitation`,
    message: `${inviterName} invited you to join "${meetingTitle}"`,
    userId,
    meetingId,
    data: {
      inviterName
    }
  });
};

/**
 * Schedule meeting reminder using Realtime Database (for backend triggers)
 */
export const scheduleMeetingReminder = async (
  userId: string,
  meetingId: string,
  meetingTitle: string,
  startTime: Date,
  reminderMinutes: number = 5
): Promise<void> => {
  try {
    const reminderTime = new Date(startTime.getTime() - reminderMinutes * 60000);
    const reminderRef = ref(realtimeDb, `reminders/${meetingId}/${userId}`);
    
    await push(reminderRef, {
      userId,
      meetingId,
      meetingTitle,
      startTime: startTime.toISOString(),
      reminderTime: reminderTime.toISOString(),
      reminderMinutes,
      createdAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error scheduling meeting reminder:', error);
    throw error;
  }
};

/**
 * Get unread notification count
 */
export const getUnreadNotificationCount = (
  userId: string,
  callback: (count: number) => void
) => {
  const q = query(
    collection(db, 'notifications'),
    where('userId', '==', userId),
    where('status', '==', NotificationStatus.UNREAD)
  );

  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    callback(querySnapshot.size);
  });

  return unsubscribe;
};
